/**
 * Keeps the header cart bubble in sync with cart changes made outside the theme.
 *
 * The theme's own components (product-form, quick-add, sticky-add-to-cart, cart-drawer,
 * component-cart-items, quick-order-list) all dispatch CartLinesUpdateEvent, which
 * cart-icon.js listens for. Third-party apps do not. Rebuy Smart Cart, for example, posts
 * straight to /cart/add.js and the bubble never hears about it, so it goes stale until a
 * full page load.
 *
 * This watches the network for Shopify cart mutations regardless of who made them, then
 * hands the resulting count to cart-icon.js rather than writing the DOM itself. That keeps
 * a single writer, so sessionStorage, the animation and the blank-above-99 behaviour all
 * stay the component's responsibility.
 *
 * fetch and XMLHttpRequest are wrapped as a chain — the original is always called — so
 * other scripts wrapping the same globals continue to work.
 */
(function () {
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)/;

  // Captured before anything is wrapped, so our own reads never re-enter the wrapper.
  const originalFetch = window.fetch;
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  /**
   * Serialises reconciliations. Overlapping cart requests must not be dropped — the last
   * one holds the truth — but they also must not race, or an out-of-order response can
   * leave a stale count on screen. So a request arriving mid-flight is queued rather than
   * discarded, and only the most recent queued payload is honoured.
   */
  let inFlight = false;
  let queued = false;

  /**
   * @returns {HTMLElement & { renderCartBubble?: (count: number, animate?: boolean) => void } | null}
   */
  function getCartIcon() {
    return document.querySelector('cart-icon');
  }

  /**
   * The count currently shown in the bubble, or null when it can't be read.
   * @returns {number | null}
   */
  function displayedCount() {
    const el = document.querySelector('[data-testid="cart-bubble"]');
    if (!el) return null;
    const text = (el.textContent ?? '').trim();
    // Blank is how the component renders counts of 100+, so it isn't a reliable number.
    if (text === '') return null;
    const parsed = parseInt(text, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  /**
   * Hands an absolute cart count to cart-icon.js.
   * @param {number} itemCount
   */
  async function syncBubble(itemCount) {
    if (typeof itemCount !== 'number' || itemCount < 0) return;

    await customElements.whenDefined('cart-icon');
    const cartIcon = getCartIcon();
    if (!cartIcon || typeof cartIcon.renderCartBubble !== 'function') return;

    // The theme's own components already render their updates. Re-rendering the same
    // number would replay the animation for no reason, so only act on a real change.
    if (displayedCount() === itemCount) return;

    cartIcon.renderCartBubble(itemCount);
  }

  /**
   * Resolves the true cart count and syncs. Prefers a count already present in the
   * response body; /cart/add.js returns only the added line, so that needs a fresh read.
   * @param {unknown} payload - Parsed response body, when available.
   */
  async function reconcile(payload) {
    if (inFlight) {
      // Don't drop it — flag that another pass is needed once this one finishes.
      queued = true;
      return;
    }
    inFlight = true;

    try {
      const count = /** @type {any} */ (payload)?.item_count;
      if (typeof count === 'number') {
        await syncBubble(count);
      } else {
        const response = await originalFetch.call(window, `${window.Shopify?.routes?.root ?? '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        if (response.ok) {
          const cart = await response.json();
          await syncBubble(cart?.item_count);
        }
      }
    } catch (error) {
      // Never let a sync failure surface to the shopper.
      console.warn('[cart-external-sync] could not reconcile cart count:', error);
    } finally {
      inFlight = false;
    }

    if (queued) {
      queued = false;
      // Always re-read the cart for a queued pass. A body captured while another request
      // was in flight may already be out of date; /cart.js is the only reliable truth.
      await reconcile(null);
    }
  }

  // --- fetch -----------------------------------------------------------------

  window.fetch = function (...args) {
    const [resource] = args;
    const url = typeof resource === 'string' ? resource : (resource?.url ?? String(resource ?? ''));

    // `this ?? window` matters: this file loads as a module, so it runs in strict mode,
    // where a bare `fetch(...)` call leaves `this` undefined. Native fetch throws
    // "Illegal invocation" if applied with undefined.
    const result = originalFetch.apply(this ?? window, args);
    if (!CART_MUTATION.test(url)) return result;

    return result.then((response) => {
      // Clone before reading so the caller still gets an unconsumed body.
      response
        .clone()
        .json()
        .then((data) => reconcile(data))
        .catch(() => reconcile(null));
      return response;
    });
  };

  // --- XMLHttpRequest --------------------------------------------------------

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__cartSyncUrl = typeof url === 'string' ? url : String(url ?? '');
    return originalOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    if (CART_MUTATION.test(this.__cartSyncUrl ?? '')) {
      this.addEventListener('load', () => {
        let data = null;
        try {
          data = JSON.parse(this.responseText);
        } catch {
          // Not JSON, or an error body — reconcile from /cart.js instead.
        }
        reconcile(data);
      });
    }
    return originalSend.apply(this, args);
  };
})();
